import React, { FC, useState, useRef, useEffect } from 'react'
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  Avatar,
  Chip,
  keyframes,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import PersonIcon from '@mui/icons-material/Person'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import CloseIcon from '@mui/icons-material/Close'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import { useTheme } from '@mui/material/styles'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { ChatMessage as APIChatMessage } from '@/shared/types/yakurekiChat'
import { useToast } from '@/shared/contexts/ToastContext'
import { AI_MESSAGES } from '@/shared/constants/messages'
import { BREAKPOINTS, RESPONSIVE_HEIGHTS } from '@/shared/constants/responsive'
import {
  useChatHistoryQuery,
  useSendChatMessageMutation,
} from '@/features/formatter'
import { useVoiceMemo } from '@/features/formatter/hooks/useVoiceMemo'
import { SoapSectionKeys } from '@/shared/types/yakurekiSoap'
import { soapLabels } from '@/shared/constants/const'

// 録音中の点滅アニメーション
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`

type InputMode = 'text' | 'recording' | 'recorded'

// セクション選択用の定数
const ALL_SECTIONS: SoapSectionKeys[] = [
  'subjectives',
  'objectives',
  'assessments',
  'educational_plans',
  'care_plans',
  'observational_plans',
]

// チップ表示用の短縮ラベル
const sectionChipLabels: Record<SoapSectionKeys, string> = {
  subjectives: 'S',
  objectives: 'O',
  assessments: 'A',
  educational_plans: 'EP',
  care_plans: 'CP',
  observational_plans: 'OP',
}

interface LocalChatMessage {
  id: string
  type: 'user' | 'ai'
  content: string
  timestamp: Date
  inputType?: 'text' | 'voice'
}

interface SoapUpdateChatProps {
  yakurekiId?: string
  onSoapUpdate?: () => Promise<void>
  isLoading?: boolean
  sx?: object
}

/**
 * 録音時間を "M:SS" 形式にフォーマット
 */
const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export const SoapUpdateChat: FC<SoapUpdateChatProps> = ({
  yakurekiId,
  onSoapUpdate,
  isLoading = false,
  sx,
}) => {
  const theme = useTheme()
  const { showToast } = useToast()
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState<LocalChatMessage[]>([])
  const [isUpdating, setIsUpdating] = useState(false)
  const [inputMode, setInputMode] = useState<InputMode>('text')
  const [selectedSections, setSelectedSections] = useState<
    Set<SoapSectionKeys>
  >(() => new Set(ALL_SECTIONS))
  const chatContainerRef = useRef<HTMLDivElement>(null)

  // 音声メモ
  const {
    isRecording,
    audioBlob,
    isConverting,
    recordingDuration,
    startRecording,
    stopRecording,
    cancelRecording,
    clearAudio,
  } = useVoiceMemo()

  // TanStack Query でチャット履歴取得
  const {
    chatHistory,
    isLoading: isLoadingHistory,
    error: chatHistoryError,
  } = useChatHistoryQuery(yakurekiId)

  // TanStack Query でメッセージ送信
  const sendMessageMutation = useSendChatMessageMutation()

  // チャット履歴が取得されたらメッセージを初期化
  useEffect(() => {
    const formattedMessages: LocalChatMessage[] = []

    // Always add initial AI greeting message first
    formattedMessages.push({
      id: 'initial-ai-message',
      type: 'ai',
      content:
        'SOAPで更新したい点があればお気軽にどうぞ。修正や追加のご要望をお聞かせください。',
      timestamp: new Date(),
    })

    if (chatHistory?.messages && chatHistory.messages.length > 0) {
      // Convert API messages to local ChatMessage format
      chatHistory.messages.forEach((apiMsg: APIChatMessage) => {
        // Add user message
        formattedMessages.push({
          id: `${apiMsg.id}-user`,
          type: 'user',
          content: apiMsg.user_query,
          timestamp: new Date(apiMsg.created_at),
          inputType: apiMsg.input_type,
        })
        // Add AI response
        formattedMessages.push({
          id: `${apiMsg.id}-ai`,
          type: 'ai',
          content: apiMsg.ai_response || AI_MESSAGES.SOAP_UPDATE_DEFAULT,
          timestamp: new Date(apiMsg.created_at),
        })
      })
    }

    setMessages(formattedMessages)
  }, [chatHistory])

  // エラーハンドリング（403, 202以外のエラーのみトースト表示）
  useEffect(() => {
    if (chatHistoryError) {
      const status = (chatHistoryError as { response?: { status?: number } })
        ?.response?.status
      // 403: チャット機能が無効、202: 処理中 - エラー表示しない
      if (status !== 403 && status !== 202) {
        showToast('チャット履歴の読み込みに失敗しました', 'error')
      }
    }
  }, [chatHistoryError, showToast])

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [messages])

  // 録音状態とinputModeの同期
  useEffect(() => {
    if (isRecording) {
      setInputMode('recording')
    } else if (audioBlob) {
      setInputMode('recorded')
    }
  }, [isRecording, audioBlob])

  // テキスト送信
  const handleSendText = async () => {
    if (!message.trim() || !yakurekiId) return

    const userMessage: LocalChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: message.trim(),
      timestamp: new Date(),
      inputType: 'text',
    }

    setMessages((prev) => [...prev, userMessage])
    setMessage('')
    setIsUpdating(true)

    try {
      const response = await sendMessageMutation.mutateAsync({
        yakurekiId,
        message: userMessage.content,
        inputType: 'text',
        allowedSections: getAllowedSections(),
      })

      const aiMessage: LocalChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response.ai_response || AI_MESSAGES.SOAP_UPDATE_DEFAULT,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, aiMessage])

      if (onSoapUpdate) {
        await onSoapUpdate()
      }
    } catch (error) {
      handleSendError(error)
    } finally {
      setIsUpdating(false)
    }
  }

  // 音声送信
  const handleSendVoice = async () => {
    if (!audioBlob || !yakurekiId) return

    const userMessage: LocalChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: `[音声メモ (${formatDuration(recordingDuration)})]`,
      timestamp: new Date(),
      inputType: 'voice',
    }

    setMessages((prev) => [...prev, userMessage])
    clearAudio()
    setInputMode('text')
    setIsUpdating(true)

    try {
      const response = await sendMessageMutation.mutateAsync({
        yakurekiId,
        audioFile: audioBlob,
        inputType: 'voice',
        allowedSections: getAllowedSections(),
      })

      // 文字起こし結果でユーザーメッセージを更新
      if (response.transcription) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === userMessage.id
              ? { ...msg, content: response.transcription as string }
              : msg
          )
        )
      }

      const aiMessage: LocalChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response.ai_response || AI_MESSAGES.SOAP_UPDATE_DEFAULT,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, aiMessage])

      if (onSoapUpdate) {
        await onSoapUpdate()
      }
    } catch (error) {
      handleSendError(error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleSendError = (error: unknown) => {
    const axiosError = error as {
      response?: { data?: { detail?: string; message?: string } }
    }
    const errorDetail =
      axiosError?.response?.data?.detail ||
      axiosError?.response?.data?.message ||
      '更新中にエラーが発生しました。もう一度お試しください。'

    const errorMessage: LocalChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'ai',
      content: errorDetail,
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, errorMessage])
    showToast('SOAPの更新に失敗しました', 'error')
  }

  const handleSend = async () => {
    if (inputMode === 'recorded') {
      await handleSendVoice()
    } else {
      await handleSendText()
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleStartRecording = async () => {
    setInputMode('recording')
    await startRecording()
  }

  const handleStopRecording = () => {
    stopRecording()
  }

  const handleCancelRecording = () => {
    cancelRecording()
    setInputMode('text')
  }

  // セクション選択トグル（最低1つは選択必須）
  const handleToggleSection = (section: SoapSectionKeys) => {
    setSelectedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) {
        if (next.size <= 1) return prev // 全解除不可
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })
  }

  // 全セクション選択中でない場合のみ allowedSections を送信
  const getAllowedSections = (): string[] | undefined => {
    if (selectedSections.size === ALL_SECTIONS.length) return undefined
    return Array.from(selectedSections)
  }

  const handleClearRecordedAudio = () => {
    clearAudio()
    setInputMode('text')
  }

  const isInputDisabled = isLoading || isUpdating
  const isTextSendDisabled =
    !message.trim() || isLoading || isUpdating || !yakurekiId
  const isVoiceSendDisabled =
    !audioBlob || isConverting || isLoading || isUpdating || !yakurekiId

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colorTokens.background.paper,
        border: `1px solid ${colorTokens.border.light}`,
        borderRadius: 2,
        overflow: 'hidden',
        [`@media (max-width: ${BREAKPOINTS.MOBILE}px)`]: {
          height: 'auto',
          minHeight: `${RESPONSIVE_HEIGHTS.CHAT_MIN}px`,
          overflow: 'visible',
        },
        ...sx,
      }}
    >
      {/* Chat messages */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 1,
          minHeight: 0,
          [`@media (max-width: ${BREAKPOINTS.MOBILE}px)`]: {
            maxHeight: `${RESPONSIVE_HEIGHTS.CHAT_AREA_MAX}px`,
            minHeight: `${RESPONSIVE_HEIGHTS.CHAT_AREA_MIN}px`,
          },
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: colorTokens.grey[100],
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colorTokens.border.medium,
            borderRadius: '3px',
          },
        }}
      >
        {isLoadingHistory ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} sx={{ color: colorTokens.grey[500] }} />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ py: 1 }}>
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  alignItems: 'flex-start',
                  justifyContent:
                    msg.type === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                {msg.type === 'ai' && (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: colorTokens.grey[500],
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '75%',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor:
                      msg.type === 'user'
                        ? colorTokens.blue[50]
                        : colorTokens.grey[100],
                    color:
                      msg.type === 'user'
                        ? colorTokens.blue[700]
                        : colorTokens.grey[900],
                    border:
                      msg.type === 'user'
                        ? `1px solid ${colorTokens.blue[200]}`
                        : 'none',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {msg.inputType === 'voice' && (
                      <MicIcon
                        sx={{
                          fontSize: 16,
                          color: colorTokens.blue[500],
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {msg.content}
                  </Typography>
                </Box>
                {msg.type === 'user' && (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: theme.palette.primary.main,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Input area */}
      <Box
        sx={{
          p: 1.5,
          borderTop: `1px solid ${colorTokens.border.light}`,
          backgroundColor: colorTokens.background.subtle,
          flexShrink: 0,
        }}
      >
        {/* セクション選択チップ */}
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            mb: 1,
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colorTokens.grey[500],
              display: 'flex',
              alignItems: 'center',
              mr: 0.5,
            }}
          >
            変更可能:
          </Typography>
          {ALL_SECTIONS.map((section) => (
            <Chip
              key={section}
              label={sectionChipLabels[section]}
              size="small"
              variant={selectedSections.has(section) ? 'filled' : 'outlined'}
              onClick={() => handleToggleSection(section)}
              title={soapLabels[section]}
              sx={{
                height: 24,
                backgroundColor: selectedSections.has(section)
                  ? colorTokens.blue[50]
                  : 'transparent',
                color: selectedSections.has(section)
                  ? colorTokens.blue[700]
                  : colorTokens.grey[500],
                borderColor: selectedSections.has(section)
                  ? colorTokens.blue[200]
                  : colorTokens.grey[300],
                '&:hover': {
                  backgroundColor: selectedSections.has(section)
                    ? colorTokens.blue[100]
                    : colorTokens.grey[100],
                },
              }}
            />
          ))}
        </Stack>

        {/* テキスト入力モード */}
        {inputMode === 'text' && (
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="SOAP修正内容を入力..."
              disabled={isInputDisabled}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colorTokens.background.paper,
                  '& fieldset': {
                    borderColor: colorTokens.border.light,
                  },
                  '&:hover fieldset': {
                    borderColor: colorTokens.border.medium,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                },
              }}
            />
            <IconButton
              onClick={handleStartRecording}
              disabled={isInputDisabled}
              size="small"
              sx={{
                width: 36,
                height: 36,
                color: isInputDisabled
                  ? colorTokens.grey[400]
                  : colorTokens.grey[600],
                '&:hover': {
                  backgroundColor: colorTokens.grey[200],
                },
              }}
            >
              <MicIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleSend}
              disabled={isTextSendDisabled}
              size="small"
              sx={{
                backgroundColor: !isTextSendDisabled
                  ? theme.palette.primary.main
                  : colorTokens.border.light,
                color: !isTextSendDisabled
                  ? colorTokens.background.paper
                  : colorTokens.grey[500],
                '&:hover': {
                  backgroundColor: !isTextSendDisabled
                    ? theme.palette.primary.dark
                    : colorTokens.border.medium,
                },
                '&.Mui-disabled': {
                  backgroundColor: colorTokens.border.light,
                  color: colorTokens.grey[500],
                },
                width: 36,
                height: 36,
              }}
            >
              {isUpdating ? (
                <CircularProgress
                  size={16}
                  sx={{ color: colorTokens.grey[500] }}
                />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        )}

        {/* 録音中モード: [● 録音中...] [■停止] [✕中断] — 停止ボタンはMicIconと同じ位置 */}
        {inputMode === 'recording' && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                color: theme.palette.error.main,
                backgroundColor: colorTokens.background.paper,
                borderRadius: 1,
                px: 2,
                py: 1,
                border: `1px solid ${theme.palette.error.light}`,
              }}
            >
              <Box sx={{ animation: `${blink} 1s ease-in-out infinite` }}>
                ●
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatDuration(recordingDuration)}
              </Typography>
              <Typography variant="body2" sx={{ color: colorTokens.grey[600] }}>
                録音中...
              </Typography>
            </Box>
            <IconButton
              onClick={handleStopRecording}
              size="small"
              sx={{
                width: 36,
                height: 36,
                backgroundColor: theme.palette.error.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }}
            >
              <StopIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleCancelRecording}
              size="small"
              sx={{
                width: 36,
                height: 36,
                color: colorTokens.grey[500],
                '&:hover': {
                  backgroundColor: colorTokens.grey[200],
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}

        {/* 録音完了モード */}
        {inputMode === 'recorded' && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                backgroundColor: colorTokens.background.paper,
                borderRadius: 1,
                px: 2,
                py: 1,
                border: `1px solid ${colorTokens.border.light}`,
              }}
            >
              <GraphicEqIcon
                sx={{
                  fontSize: 20,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography variant="body2" sx={{ color: colorTokens.grey[700] }}>
                音声メモ ({formatDuration(recordingDuration)})
              </Typography>
              {isConverting && (
                <CircularProgress
                  size={14}
                  sx={{ color: colorTokens.grey[400] }}
                />
              )}
              <IconButton
                onClick={handleClearRecordedAudio}
                size="small"
                sx={{
                  ml: 'auto',
                  width: 28,
                  height: 28,
                  color: colorTokens.grey[500],
                  '&:hover': {
                    backgroundColor: colorTokens.grey[200],
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <IconButton
              onClick={handleSend}
              disabled={isVoiceSendDisabled}
              size="small"
              sx={{
                backgroundColor: !isVoiceSendDisabled
                  ? theme.palette.primary.main
                  : colorTokens.border.light,
                color: !isVoiceSendDisabled
                  ? colorTokens.background.paper
                  : colorTokens.grey[500],
                '&:hover': {
                  backgroundColor: !isVoiceSendDisabled
                    ? theme.palette.primary.dark
                    : colorTokens.border.medium,
                },
                '&.Mui-disabled': {
                  backgroundColor: colorTokens.border.light,
                  color: colorTokens.grey[500],
                },
                width: 36,
                height: 36,
              }}
            >
              {isUpdating ? (
                <CircularProgress
                  size={16}
                  sx={{ color: colorTokens.grey[500] }}
                />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        )}
      </Box>
    </Paper>
  )
}
